import React, { useEffect, useState } from "react";
import { ShieldCheck, Plus, CheckCircle2, FileText } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, EmptyState } from "../components/ui";

export default function Policies() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ title: "", category: "HR", version: "1.0", body: "" });
  const [busy, setBusy] = useState(false);
  const isAdmin = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => api.get("/policies").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const ack = async (id) => { await api.post(`/policies/${id}/ack`); load(); };
  const create = async (e) => { e.preventDefault(); setBusy(true); try { await api.post("/policies", form); setOpen(false); setForm({ title: "", category: "HR", version: "1.0", body: "" }); load(); } catch (err) { window.alert(apiError(err.response?.data?.detail)); } finally { setBusy(false); } };
  if (!rows) return <Loading />;

  return (
    <div>
      <PageHeader title="Policies" subtitle="Company policies & acknowledgements">
        {isAdmin && <button data-testid="new-policy" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> New Policy</button>}
      </PageHeader>
      {rows.length === 0 ? <EmptyState icon={ShieldCheck} title="No policies" /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="policies">
          {rows.map((p) => (
            <Card key={p.id} className="flex flex-col p-5" data-testid={`policy-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2"><span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent"><FileText size={18} /></span>
                  <div><p className="font-semibold text-slate-800 dark:text-slate-100">{p.title}</p><p className="text-xs text-slate-400">{p.category} · v{p.version}</p></div>
                </div>
                {p.acknowledged && <Badge tone="ok"><CheckCircle2 size={11} className="mr-0.5 inline" />Acknowledged</Badge>}
              </div>
              <p className="mt-3 flex-1 text-sm text-slate-500 dark:text-slate-400 line-clamp-3">{p.body}</p>
              <div className="mt-4 flex items-center justify-between">
                {isAdmin && <span className="text-xs text-slate-400">{p.ackCount} acknowledged</span>}
                <button onClick={() => setView(p)} className="text-xs font-semibold text-accent hover:underline">Read</button>
                {!p.acknowledged && <button data-testid={`policy-ack-${p.id}`} onClick={() => ack(p.id)} className="btn-accent px-3 py-1.5 text-sm">Acknowledge</button>}
              </div>
            </Card>
          ))}
        </div>
      )}
      <Modal open={!!view} onClose={() => setView(null)} title={view?.title || ""} footer={<button onClick={() => setView(null)} className="btn-accent">Close</button>}>
        {view && <div className="space-y-2"><Badge tone="info">{view.category} · v{view.version}</Badge><p className="whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">{view.body}</p></div>}
      </Modal>
      <Modal open={open} onClose={() => setOpen(false)} title="New Policy"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="pol-form" data-testid="pol-submit" disabled={busy} className="btn-accent">Publish</button></>}>
        <form id="pol-form" onSubmit={create} className="space-y-4">
          <div><label className="label">Title *</label><input required data-testid="pol-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Category</label><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" /></div>
            <div><label className="label">Version</label><input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} className="input" /></div>
          </div>
          <div><label className="label">Body *</label><textarea required data-testid="pol-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input h-28 py-2" /></div>
        </form>
      </Modal>
    </div>
  );
}
